import { Navigate, Route, Routes, Link, useLocation, useNavigate } from 'react-router-dom'
import { useNexusStore } from '../../stores/nexusStore'
import nexusApi from '../../services/nexusApi'
import NexusDashboard from './NexusDashboard'
import NexusTenants from './NexusTenants'
import NexusTenantDetail from './NexusTenantDetail'
import NexusRevenue from './NexusRevenue'
import NexusSupport from './NexusSupport'
import NexusPlatformHealth from './NexusPlatformHealth'
import NexusSalesDemo from './NexusSalesDemo'
import NexusUpdates from './NexusUpdates'
import {
  LayoutDashboard, Building2, Users, DollarSign, LifeBuoy,
  Activity, LogOut, Shield, ChevronRight, Bell, Flag, FileText, Play, RefreshCw
} from 'lucide-react'

const NX = {
  bg: '#0D1117',
  surface: '#161B22',
  surface2: '#1C2330',
  border: '#2A3441',
  text: '#E6EDF3',
  textDim: '#8B949E',
  accent: '#0066FF',
  green: '#3DB54A',
  amber: '#F5921E',
  red: '#EF4444',
}

function NavItem({ to, icon: Icon, label, end = false }: { to: string; icon: any; label: string; end?: boolean }) {
  const loc = useLocation()
  const active = end ? loc.pathname === to : loc.pathname.startsWith(to)
  return (
    <Link to={to} style={{
      display: 'flex', alignItems: 'center', gap: '0.625rem',
      padding: '0.5rem 0.875rem', borderRadius: 8, textDecoration: 'none',
      color: active ? '#fff' : NX.textDim,
      background: active ? 'rgba(0,102,255,0.18)' : 'transparent',
      fontSize: '0.875rem', fontWeight: active ? 600 : 400,
      transition: 'all 0.15s', marginBottom: 2,
    }}>
      <Icon size={16} color={active ? NX.accent : NX.textDim} />
      {label}
    </Link>
  )
}

function NexusGuard({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useNexusStore()
  if (!isAuthenticated) return <Navigate to="/nexus/login" replace />
  return <>{children}</>
}

export default function NexusApp() {
  const { admin, logout } = useNexusStore()
  const navigate = useNavigate()

  async function handleLogout() {
    try { await nexusApi.post('/auth/logout') } catch {}
    logout()
    navigate('/nexus/login')
  }

  return (
    <NexusGuard>
      <div style={{
        minHeight: '100vh', background: NX.bg, display: 'flex',
        fontFamily: "'Inter', -apple-system, sans-serif", color: NX.text,
      }}>
        {/* Sidebar */}
        <aside style={{
          width: 240, background: NX.surface, borderRight: `1px solid ${NX.border}`,
          display: 'flex', flexDirection: 'column', flexShrink: 0, position: 'fixed',
          top: 0, left: 0, bottom: 0, zIndex: 100, overflowY: 'auto',
        }}>
          {/* Logo */}
          <div style={{
            padding: '1.25rem 1rem', borderBottom: `1px solid ${NX.border}`,
            display: 'flex', alignItems: 'center', gap: '0.75rem',
          }}>
            <div style={{
              width: 34, height: 34, borderRadius: 8, background: NX.accent,
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              <Shield size={18} color="#fff" />
            </div>
            <div>
              <div style={{ color: NX.text, fontWeight: 700, fontSize: '0.9375rem', lineHeight: 1.2 }}>NEXUS</div>
              <div style={{ color: NX.textDim, fontSize: '0.6875rem' }}>Operations Platform</div>
            </div>
          </div>

          {/* Nav */}
          <nav style={{ padding: '0.875rem 0.5rem', flex: 1 }}>
            <div style={{ color: NX.textDim, fontSize: '0.6875rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', padding: '0 0.375rem 0.5rem' }}>
              Overview
            </div>
            <NavItem to="/nexus/dashboard" icon={LayoutDashboard} label="Dashboard" end />

            <div style={{ color: NX.textDim, fontSize: '0.6875rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', padding: '1rem 0.375rem 0.5rem' }}>
              Customers
            </div>
            <NavItem to="/nexus/tenants" icon={Building2} label="Tenants" />
            <NavItem to="/nexus/users" icon={Users} label="All Users" />

            <div style={{ color: NX.textDim, fontSize: '0.6875rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', padding: '1rem 0.375rem 0.5rem' }}>
              Business
            </div>
            <NavItem to="/nexus/revenue" icon={DollarSign} label="Revenue" />
            <NavItem to="/nexus/support" icon={LifeBuoy} label="Support" />

            <div style={{ color: NX.textDim, fontSize: '0.6875rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', padding: '1rem 0.375rem 0.5rem' }}>
              Platform
            </div>
            <NavItem to="/nexus/platform/health" icon={Activity} label="Health & Errors" />
            <NavItem to="/nexus/platform/flags" icon={Flag} label="Feature Flags" />
            <NavItem to="/nexus/platform/announcements" icon={Bell} label="Announcements" />
            <NavItem to="/nexus/platform/audit" icon={FileText} label="Audit Log" />
            <NavItem to="/nexus/updates" icon={RefreshCw} label="Platform Updates" />

            <div style={{ color: NX.textDim, fontSize: '0.6875rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', padding: '1rem 0.375rem 0.5rem' }}>
              Sales
            </div>
            <NavItem to="/nexus/sales-demo" icon={Play} label="Sales Demo Mode" />
          </nav>

          {/* User footer */}
          <div style={{
            padding: '0.875rem 1rem', borderTop: `1px solid ${NX.border}`,
            display: 'flex', alignItems: 'center', gap: '0.625rem',
          }}>
            <div style={{
              width: 32, height: 32, borderRadius: '50%', background: NX.accent,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '0.8125rem', fontWeight: 700, color: '#fff', flexShrink: 0,
            }}>
              {admin?.name?.charAt(0) || 'A'}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ color: NX.text, fontSize: '0.8125rem', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {admin?.name || 'Admin'}
              </div>
              <div style={{ color: NX.textDim, fontSize: '0.6875rem', textTransform: 'capitalize' }}>{admin?.role}</div>
            </div>
            <button onClick={handleLogout} style={{
              background: 'none', border: 'none', cursor: 'pointer', color: NX.textDim,
              padding: 4, display: 'flex', borderRadius: 4,
            }} title="Logout">
              <LogOut size={15} />
            </button>
          </div>
        </aside>

        {/* Main content */}
        <main style={{ marginLeft: 240, flex: 1, minHeight: '100vh', padding: '0' }}>
          <Routes>
            <Route index element={<Navigate to="/nexus/dashboard" replace />} />
            <Route path="dashboard" element={<NexusDashboard />} />
            <Route path="tenants" element={<NexusTenants />} />
            <Route path="tenants/:id" element={<NexusTenantDetail />} />
            <Route path="users" element={<NexusTenants usersMode />} />
            <Route path="revenue" element={<NexusRevenue />} />
            <Route path="support" element={<NexusSupport />} />
            <Route path="platform/:tab" element={<NexusPlatformHealth />} />
            <Route path="platform" element={<Navigate to="/nexus/platform/health" replace />} />
            <Route path="sales-demo" element={<NexusSalesDemo />} />
            <Route path="updates" element={<NexusUpdates />} />
            <Route path="*" element={<Navigate to="/nexus/dashboard" replace />} />
          </Routes>
        </main>
      </div>
    </NexusGuard>
  )
}
